import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Database,
  Eye,
  Loader2,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Zap,
} from 'lucide-react';
import {
  fetchR2FileList,
  fetchProcessedText,
  fetchExtractedArticle,
  fetchContractDetail,
  fetchPipelineHealth,
  restoreStructure,
  mergeContractStructure,
  reextractMissingBatch,
  getBatchStatus,
  processBatchResults,
  cancelBatch,
  embedClausesChunked,
  R2FileListResponse,
  ExtractedArticleFromR2,
  PipelineHealthResponse,
  PipelineArticleStatus,
  BatchStatusResponse,
} from '../../services/api';

export interface PipelineViewerProps {
  contractId: string;
  contractName: string;
}

interface ArticleSummary {
  number: string;
  title: string;
  sectionCount: number;
  clauseCount: number;
  charCount: number;
}

interface LoadingState {
  ocr: boolean;
  files: boolean;
  articles: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function highlightHeaders(text: string): React.ReactNode[] {
  const pattern = /(ARTICLE\s+\d+|PART\s+\d+)/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span
        key={match.index}
        className="bg-yellow-200 text-yellow-900 px-1 rounded font-semibold"
      >
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatCard: React.FC<{ label: string; value: string | number; loading?: boolean }> = ({
  label,
  value,
  loading,
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</div>
    {loading ? (
      <div className="mt-1 h-7 w-16 bg-gray-200 animate-pulse rounded" />
    ) : (
      <div className="mt-1 text-xl font-bold text-gray-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    )}
  </div>
);

const CollapsibleSection: React.FC<{
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  testId?: string;
}> = ({ title, subtitle, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      data-testid={testId}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-500" />
          )}
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {subtitle && <span className="text-xs text-gray-500 ml-2">{subtitle}</span>}
        </div>
      </button>
      {open && <div className="border-t border-gray-200">{children}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Layer 1: OCR Output
// ---------------------------------------------------------------------------

const OCRLayer: React.FC<{
  contractId: string;
  ocrText: string | null;
  ocrLoading: boolean;
  onLoad: () => void;
}> = ({ contractId, ocrText, ocrLoading, onLoad }) => {
  const [start, setStart] = useState(0);
  const [length, setLength] = useState(3000);
  const [slice, setSlice] = useState<string | null>(null);

  const handleShow = () => {
    if (!ocrText) {
      onLoad();
      return;
    }
    setSlice(ocrText.slice(start, start + length));
  };

  // When ocrText arrives, update slice
  useEffect(() => {
    if (ocrText && slice === null) {
      setSlice(ocrText.slice(start, start + length));
    }
  }, [ocrText, slice, start, length]);

  return (
    <CollapsibleSection title="OCR Output (processed.json)" testId="pipeline-ocr-layer">
      <div className="p-5 space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Start char</label>
            <input
              type="number"
              min={0}
              value={start}
              onChange={(e) => setStart(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-28 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              data-testid="ocr-start-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Length</label>
            <input
              type="number"
              min={100}
              max={50000}
              value={length}
              onChange={(e) => setLength(Math.max(100, parseInt(e.target.value) || 3000))}
              className="w-28 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              data-testid="ocr-length-input"
            />
          </div>
          <button
            onClick={handleShow}
            disabled={ocrLoading}
            className="inline-flex items-center px-4 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-primary-300 text-sm font-medium transition-colors"
            data-testid="ocr-show-button"
          >
            {ocrLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-1.5" />
                Show
              </>
            )}
          </button>
          {ocrText && (
            <span className="text-xs text-gray-500">
              Total: {ocrText.length.toLocaleString()} chars
            </span>
          )}
        </div>

        {slice !== null && (
          <pre
            className="font-mono text-xs leading-relaxed bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto whitespace-pre-wrap break-words"
            data-testid="ocr-text-output"
          >
            {highlightHeaders(slice)}
          </pre>
        )}
      </div>
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// Layer 2: R2 File Inventory
// ---------------------------------------------------------------------------

const R2FilesLayer: React.FC<{
  files: R2FileListResponse['objects'];
  loading: boolean;
}> = ({ files, loading }) => (
  <CollapsibleSection
    title="R2 Files"
    subtitle={loading ? undefined : `${files.length} files`}
    testId="pipeline-r2-layer"
  >
    {loading ? (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 mr-2" />
        <span className="text-sm text-gray-500">Loading file inventory...</span>
      </div>
    ) : files.length === 0 ? (
      <div className="text-center py-8 text-sm text-gray-500">No files found.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" data-testid="r2-files-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                File Path
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Size
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Uploaded
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {files.map((file, i) => (
              <tr
                key={file.key}
                className={i % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'}
                data-testid={`r2-file-row-${i}`}
              >
                <td className="px-5 py-2.5 text-sm font-mono text-gray-700 truncate max-w-md" title={file.key}>
                  {file.key}
                </td>
                <td className="px-5 py-2.5 text-sm text-gray-500 text-right whitespace-nowrap">
                  {formatBytes(file.size)}
                </td>
                <td className="px-5 py-2.5 text-sm text-gray-500 text-right whitespace-nowrap">
                  {formatDate(file.uploaded)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </CollapsibleSection>
);

// ---------------------------------------------------------------------------
// Layer 3: Extracted Articles
// ---------------------------------------------------------------------------

const ArticleDetail: React.FC<{ data: ExtractedArticleFromR2 }> = ({ data }) => {
  const { article } = data;

  return (
    <div className="px-5 pb-5 pt-3 space-y-3 border-t border-gray-100 bg-gray-50/50">
      {article.summary && (
        <p className="text-sm text-gray-600 italic">{article.summary}</p>
      )}

      {data.extractedAt && (
        <div className="text-xs text-gray-400">
          Extracted: {formatDate(data.extractedAt)}
          {data.processingTime != null && ` (${(data.processingTime / 1000).toFixed(1)}s)`}
          {data.tokensUsed && ` | Tokens: ${data.tokensUsed.input + data.tokensUsed.output}`}
          {data.chunked && data.totalChunks && ` | ${data.totalChunks} chunks`}
        </div>
      )}

      {article.sections.map((section) => (
        <div key={section.number} className="ml-2">
          <h4 className="text-sm font-semibold text-primary-700">
            Section {section.number}: {section.title}
          </h4>
          <div className="ml-4 mt-1 space-y-1.5">
            {section.clauses.map((clause, ci) => (
              <div key={ci} className="text-sm text-gray-700">
                {clause.number && (
                  <span className="text-primary-500 font-medium mr-1">
                    {clause.number}
                  </span>
                )}
                <span className="text-gray-600">{clause.text}</span>
                {clause.cfrCitations && clause.cfrCitations.length > 0 && (
                  <span className="ml-2">
                    {clause.cfrCitations.map((cit, j) => (
                      <span
                        key={j}
                        className="inline-block bg-yellow-100 text-yellow-700 text-xs px-1.5 py-0.5 rounded ml-1"
                      >
                        {cit}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ExtractedArticlesLayer: React.FC<{
  articleList: ArticleSummary[];
  loading: boolean;
  expandedArticle: string | null;
  expandedArticleData: ExtractedArticleFromR2 | null;
  expandedArticleLoading: boolean;
  onToggleArticle: (num: string) => void;
}> = ({
  articleList,
  loading,
  expandedArticle,
  expandedArticleData,
  expandedArticleLoading,
  onToggleArticle,
}) => {
  const maxCharCount = Math.max(1, ...articleList.map((a) => a.charCount));

  return (
    <CollapsibleSection
      title="Extracted Articles"
      subtitle={loading ? undefined : `${articleList.length} articles`}
      testId="pipeline-articles-layer"
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 mr-2" />
          <span className="text-sm text-gray-500">Loading articles...</span>
        </div>
      ) : articleList.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">No extracted articles found.</div>
      ) : (
        <div>
          <div className="px-5 py-2 text-xs text-gray-500">
            Click any article to expand its sections and clauses.
          </div>
          <div className="divide-y divide-gray-100">
            {articleList.map((article) => {
              const isExpanded = expandedArticle === article.number;

              return (
                <div key={article.number}>
                  <button
                    onClick={() => onToggleArticle(article.number)}
                    className={`w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left ${
                      isExpanded ? 'bg-primary-50/50' : ''
                    }`}
                    data-testid={`pipeline-article-${article.number}`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    )}

                    <span className="bg-primary-100 text-primary-700 font-bold rounded px-2 py-1 text-xs flex-shrink-0">
                      {article.number}
                    </span>

                    <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                      {article.title}
                    </span>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-gray-500">
                        {article.sectionCount}s / {article.clauseCount}c
                      </span>
                      <span className="text-xs text-gray-400">
                        {article.charCount.toLocaleString()} chars
                      </span>
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-400 rounded-full"
                          style={{ width: `${(article.charCount / maxCharCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    expandedArticleLoading ? (
                      <div className="flex items-center justify-center py-6 border-t border-gray-100 bg-gray-50/50">
                        <Loader2 className="h-4 w-4 animate-spin text-primary-600 mr-2" />
                        <span className="text-sm text-gray-500">Loading article detail...</span>
                      </div>
                    ) : expandedArticleData ? (
                      <ArticleDetail data={expandedArticleData} />
                    ) : null
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// Pipeline Health Panel
// ---------------------------------------------------------------------------

const StatusBadge: React.FC<{ status: 'ok' | 'not_stored' | 'missing'; coveredBy?: 'definitions' | 'acronyms'; errorType?: PipelineArticleStatus['errorType']; errorMessage?: string }> = ({ status, coveredBy, errorType, errorMessage }) => {
  const styles: Record<string, string> = {
    ok: 'bg-green-100 text-green-800',
    not_stored: 'bg-yellow-100 text-yellow-800',
    missing: 'bg-red-100 text-red-800',
  };

  let label: string;
  let badgeStyle = styles[status];
  if (coveredBy) {
    label = coveredBy === 'definitions' ? 'Definitions' : 'Acronyms';
    badgeStyle = 'bg-blue-100 text-blue-800';
  } else if (status !== 'missing' || !errorType) {
    const defaultLabels: Record<string, string> = { ok: 'OK', not_stored: 'Not stored', missing: 'Missing' };
    label = defaultLabels[status] || status;
  } else {
    const errorLabels: Record<string, string> = {
      boundary_not_found: 'Boundary fail',
      extraction_timeout: 'Timeout',
      api_error: 'API error',
      parse_failed: 'Parse error',
      max_tokens: 'Truncated',
      expired: 'Expired',
      unknown: 'Error',
    };
    label = errorLabels[errorType] || 'Error';
  }

  return (
    <span
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${badgeStyle}`}
      title={coveredBy ? `Covered by ${coveredBy} extraction` : (errorMessage || undefined)}
    >
      {label}
    </span>
  );
};

const PipelineHealthLayer: React.FC<{
  contractId: string;
  health: PipelineHealthResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onRestore: () => void;
  onReextract: () => void;
  onEmbed: () => void;
  actionLoading: string | null;
  batchMessage: string | null;
  embedProgress: string | null;
  contractStatus: string | null;
}> = ({ contractId, health, loading, error, onRefresh, onRestore, onReextract, onEmbed, actionLoading, batchMessage, embedProgress, contractStatus }) => {
  const [batchStatus, setBatchStatus] = useState<BatchStatusResponse | null>(null);
  const [batchPolling, setBatchPolling] = useState(false);
  const [processingResults, setProcessingResults] = useState(false);
  const [processMessage, setProcessMessage] = useState<string | null>(null);
  const [cancelingBatch, setCancelingBatch] = useState(false);

  const handleCancelBatch = async () => {
    if (cancelingBatch) return;
    setCancelingBatch(true);
    try {
      await cancelBatch(contractId);
      setBatchStatus(null);
      setBatchPolling(false);
      onRefresh(); // Refresh health data to clear activeBatch
    } catch (err) {
      console.error('Failed to cancel batch:', err);
    } finally {
      setCancelingBatch(false);
    }
  };

  // Poll batch status when activeBatch is present
  useEffect(() => {
    if (!health?.activeBatch) {
      setBatchStatus(null);
      setBatchPolling(false);
      return;
    }

    let cancelled = false;

    const pollStatus = async () => {
      setBatchPolling(true);
      try {
        const status = await getBatchStatus(contractId);
        if (!cancelled) setBatchStatus(status);
      } catch (e) {
        console.error('[PipelineHealth] Failed to poll batch status:', e);
      } finally {
        if (!cancelled) setBatchPolling(false);
      }
    };

    // Poll immediately, then every 15 seconds
    pollStatus();
    const interval = setInterval(pollStatus, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [health?.activeBatch, contractId]);

  // Auto-process results when batch is ended
  const handleProcessResults = useCallback(async () => {
    setProcessingResults(true);
    setProcessMessage('Processing batch results...');
    try {
      let offset = 0;
      let totalStored = 0;
      let totalFailed = 0;
      // Process in pages until complete
      while (true) {
        const result = await processBatchResults(contractId, offset);
        totalStored += result.stored || 0;
        totalFailed += result.failed || 0;
        if (result.complete) break;
        offset = result.nextOffset || 0;
        setProcessMessage(`Processing batch results... stored ${totalStored} so far`);
      }
      setProcessMessage(`Done: ${totalStored} stored, ${totalFailed} failed`);
      // Refresh health data to clear activeBatch
      onRefresh();
    } catch (e) {
      console.error('[PipelineHealth] Failed to process batch results:', e);
      setProcessMessage(`Error processing results: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setProcessingResults(false);
    }
  }, [contractId, onRefresh]);

  if (loading) {
    return (
      <CollapsibleSection title="Pipeline Health" defaultOpen testId="pipeline-health-layer">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 mr-2" />
          <span className="text-sm text-gray-500">Checking pipeline health...</span>
        </div>
      </CollapsibleSection>
    );
  }

  if (error) {
    return (
      <CollapsibleSection title="Pipeline Health" defaultOpen testId="pipeline-health-layer">
        <div className="p-5">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      </CollapsibleSection>
    );
  }

  if (!health) return null;

  // Derive action button visibility from articleStatuses (the only frontend-side logic needed)
  const missingArticles = health.articleStatuses.filter(a => a.status === 'missing');
  const notStoredCount = health.articleStatuses.filter(a => a.status === 'not_stored').length;
  const allGood = health.issues.length === 0;

  return (
    <CollapsibleSection
      title="Pipeline Health"
      subtitle={allGood ? 'All OK' : `${health.issues.length} issue${health.issues.length !== 1 ? 's' : ''}`}
      defaultOpen={!allGood}
      testId="pipeline-health-layer"
    >
      <div className="p-5 space-y-4">
        {/* All good banner */}
        {allGood && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800 font-medium">
              All {health.tocArticles} articles processed successfully
            </span>
          </div>
        )}

        {/* Issue banners — rendered directly from backend issues array */}
        {health.issues.map((issue, i) => {
          const isError = issue.includes('could not be located') || issue.includes('failed during extraction');
          return (
            <div key={i} className={`${isError ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-3 flex items-center gap-2`}>
              {isError ? (
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
              )}
              <span className={`text-sm ${isError ? 'text-red-800' : 'text-yellow-800'}`}>{issue}</span>
            </div>
          );
        })}

        {/* Persistent batch-in-progress banner (from R2 metadata) */}
        {health.activeBatch && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 text-blue-600 flex-shrink-0 animate-spin" />
              <span className="text-sm text-blue-800 font-medium">
                Batch extraction in progress: {health.activeBatch.totalArticles} article{health.activeBatch.totalArticles !== 1 ? 's' : ''} submitted at{' '}
                {formatDate(health.activeBatch.submittedAt)}
              </span>
            </div>
            {batchStatus && batchStatus.processingStatus && (
              <div className="text-sm text-blue-700 ml-6">
                Status: <span className="font-medium">{batchStatus.processingStatus}</span>
                {batchStatus.total != null && (
                  <span className="ml-3">
                    {batchStatus.processing != null && batchStatus.processing > 0 && (
                      <span className="mr-2">Processing: {batchStatus.processing}/{batchStatus.total}</span>
                    )}
                    {batchStatus.succeeded != null && batchStatus.succeeded > 0 && (
                      <span className="mr-2 text-green-700">Succeeded: {batchStatus.succeeded}</span>
                    )}
                    {batchStatus.errored != null && batchStatus.errored > 0 && (
                      <span className="mr-2 text-red-700">Errored: {batchStatus.errored}</span>
                    )}
                    {batchStatus.expired != null && batchStatus.expired > 0 && (
                      <span className="text-yellow-700">Expired: {batchStatus.expired}</span>
                    )}
                  </span>
                )}
                {batchPolling && <Loader2 className="h-3 w-3 text-blue-400 inline ml-2 animate-spin" />}
              </div>
            )}
            {batchStatus?.isStale && (
              <div className="ml-6 flex items-center gap-2 mt-1">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-xs text-amber-700 font-medium">
                  Batch appears stuck ({batchStatus.batchAgeMinutes}min old, 0 progress)
                </span>
              </div>
            )}
            {batchStatus?.processingStatus === 'in_progress' && (
              <div className="ml-6 mt-1">
                <button
                  onClick={handleCancelBatch}
                  disabled={cancelingBatch}
                  className="inline-flex items-center px-2.5 py-1 border border-red-300 rounded-md text-xs font-medium text-red-800 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors"
                >
                  {cancelingBatch ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <AlertCircle className="h-3 w-3 mr-1" />
                  )}
                  Cancel Batch
                </button>
              </div>
            )}
            {batchStatus?.processingStatus === 'ended' && (
              <div className="ml-6 flex items-center gap-2">
                <span className="text-sm text-green-700 font-medium">Batch complete</span>
                <button
                  onClick={handleProcessResults}
                  disabled={processingResults}
                  className="inline-flex items-center px-2.5 py-1 border border-green-300 rounded-md text-xs font-medium text-green-800 bg-green-50 hover:bg-green-100 disabled:opacity-50 transition-colors"
                >
                  {processingResults ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle className="h-3 w-3 mr-1" />
                  )}
                  Process Results
                </button>
              </div>
            )}
            {processMessage && (
              <div className="text-xs text-blue-600 ml-6">{processMessage}</div>
            )}
          </div>
        )}

        {/* Ephemeral batch submission feedback */}
        {batchMessage && !health.activeBatch && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
            <Loader2 className="h-4 w-4 text-blue-600 flex-shrink-0 animate-spin" />
            <span className="text-sm text-blue-800">{batchMessage}</span>
          </div>
        )}

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{health.tocArticles}</div>
            <div className="text-xs text-gray-500 mt-0.5">TOC Articles</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{health.r2ArticleFiles}</div>
            <div className="text-xs text-gray-500 mt-0.5">R2 Files</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{health.dbArticles}</div>
            <div className="text-xs text-gray-500 mt-0.5">In Database</div>
          </div>
        </div>

        {/* Definitions & Acronyms stats */}
        {(health.definitionsR2Count != null && health.definitionsR2Count > 0) || (health.acronymsR2Count != null && health.acronymsR2Count > 0) ? (
          <div className="grid grid-cols-2 gap-3">
            {health.definitionsR2Count != null && health.definitionsR2Count > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-blue-900">Definitions</div>
                  <div className="text-xs text-blue-600 mt-0.5">
                    {health.definitionsR2Count} in R2 &middot; {health.definitionsDbCount ?? 0} in DB
                  </div>
                </div>
                {health.definitionsDbCount != null && health.definitionsDbCount > 0 ? (
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <span title="Not stored in database"><AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" /></span>
                )}
              </div>
            )}
            {health.acronymsR2Count != null && health.acronymsR2Count > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-blue-900">Acronyms</div>
                  <div className="text-xs text-blue-600 mt-0.5">
                    {health.acronymsR2Count} in R2 &middot; {health.acronymsDbCount ?? 0} in DB
                  </div>
                </div>
                {health.acronymsDbCount != null && health.acronymsDbCount > 0 ? (
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <span title="Not stored in database"><AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" /></span>
                )}
              </div>
            )}
          </div>
        ) : null}

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            disabled={!!actionLoading}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${actionLoading === 'refresh' ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {notStoredCount > 0 && (
            <button
              onClick={onRestore}
              disabled={!!actionLoading}
              className="inline-flex items-center px-3 py-1.5 border border-yellow-300 rounded-md text-sm font-medium text-yellow-800 bg-yellow-50 hover:bg-yellow-100 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'restore' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Database className="h-3.5 w-3.5 mr-1.5" />
              )}
              Re-store Structure
            </button>
          )}
          {missingArticles.length > 0 && (
            <button
              onClick={onReextract}
              disabled={!!actionLoading}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-800 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'reextract' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <FileText className="h-3.5 w-3.5 mr-1.5" />
              )}
              Re-extract {missingArticles.length} Missing Article{missingArticles.length !== 1 ? 's' : ''} (Batch)
            </button>
          )}
          {health.dbArticles > 0 && contractStatus !== 'embedded' && contractStatus !== 'linked' && contractStatus !== 'article7_analyzed' && contractStatus !== 'ready' && (
            <button
              onClick={onEmbed}
              disabled={!!actionLoading}
              className="inline-flex items-center px-3 py-1.5 border border-purple-300 rounded-md text-sm font-medium text-purple-800 bg-purple-50 hover:bg-purple-100 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'embed' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Zap className="h-3.5 w-3.5 mr-1.5" />
              )}
              Embed Clauses
            </button>
          )}
        </div>

        {/* Embedding progress */}
        {embedProgress && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-2">
            {actionLoading === 'embed' ? (
              <Loader2 className="h-4 w-4 text-purple-600 flex-shrink-0 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4 text-purple-600 flex-shrink-0" />
            )}
            <span className="text-sm text-purple-800">{embedProgress}</span>
          </div>
        )}

        {/* Per-article status table */}
        {health.articleStatuses.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" data-testid="pipeline-health-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">R2 File</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">In DB</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Error Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {health.articleStatuses.map((art, i) => (
                  <tr key={art.number} className={i % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'}>
                    <td className="px-4 py-2 text-sm font-mono font-medium text-gray-900">{art.number}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 truncate max-w-xs" title={art.title}>
                      {art.title}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {art.inR2 ? (
                        <CheckCircle className="h-4 w-4 text-green-500 inline" />
                      ) : (
                        <span className="text-gray-300">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {art.inDB ? (
                        <CheckCircle className="h-4 w-4 text-green-500 inline" />
                      ) : (
                        <span className="text-gray-300">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <StatusBadge status={art.status} coveredBy={art.coveredBy} errorType={art.errorType} errorMessage={art.errorMessage} />
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 max-w-xs">
                      {art.coveredBy ? (
                        <span className="text-blue-600">Extracted via {art.coveredBy} pipeline</span>
                      ) : art.status === 'missing' && art.errorMessage ? (
                        <div>
                          <div className="text-gray-700 truncate" title={art.errorMessage}>{art.errorMessage}</div>
                          <div className="text-gray-400 mt-0.5">
                            {art.attemptCount && <span>Attempts: {art.attemptCount}</span>}
                            {art.lastAttemptAt && <span className="ml-2">Last: {formatDate(art.lastAttemptAt)}</span>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-300">&mdash;</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const PipelineViewer: React.FC<PipelineViewerProps> = ({
  contractId,
  contractName,
}) => {
  // State
  const [ocrText, setOcrText] = useState<string | null>(null);
  const [r2Files, setR2Files] = useState<R2FileListResponse['objects']>([]);
  const [articleList, setArticleList] = useState<ArticleSummary[]>([]);
  const [fullArticles, setFullArticles] = useState<Map<string, ExtractedArticleFromR2>>(new Map());
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [expandedArticleData, setExpandedArticleData] = useState<ExtractedArticleFromR2 | null>(null);
  const [expandedArticleLoading, setExpandedArticleLoading] = useState(false);
  const [loading, setLoading] = useState<LoadingState>({ ocr: false, files: true, articles: true });
  const [error, setError] = useState<string | null>(null);

  // Pipeline Health state
  const [healthData, setHealthData] = useState<PipelineHealthResponse | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [batchMessage, setBatchMessage] = useState<string | null>(null);
  const [embedProgress, setEmbedProgress] = useState<string | null>(null);
  const [contractStatus, setContractStatus] = useState<string | null>(null);

  // Stats
  const [stats, setStats] = useState({
    ocrChars: 0,
    totalArticles: 0,
    totalSections: 0,
    totalClauses: 0,
    r2FileCount: 0,
  });

  // Load pipeline health data
  const loadHealthData = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const data = await fetchPipelineHealth(contractId);
      setHealthData(data);
    } catch (e) {
      console.error('[PipelineViewer] Failed to load pipeline health:', e);
      setHealthError(e instanceof Error ? e.message : 'Failed to load pipeline health');
    } finally {
      setHealthLoading(false);
    }
  }, [contractId]);

  // Action handlers
  const handleRefreshHealth = useCallback(async () => {
    setActionLoading('refresh');
    await loadHealthData();
    setActionLoading(null);
  }, [loadHealthData]);

  const handleRestore = useCallback(async () => {
    setActionLoading('restore');
    try {
      // Merge individual article files into structure.json first
      await mergeContractStructure(contractId);
      // Then store merged structure into Supabase
      await restoreStructure(contractId);
      await loadHealthData();
    } catch (e) {
      console.error('[PipelineViewer] Failed to re-store structure:', e);
      setHealthError(e instanceof Error ? e.message : 'Failed to re-store structure');
    } finally {
      setActionLoading(null);
    }
  }, [contractId, loadHealthData]);

  const handleEmbed = useCallback(async () => {
    setActionLoading('embed');
    setEmbedProgress('Starting clause embedding...');
    try {
      const result = await embedClausesChunked(
        contractId,
        (embedded, total) => {
          setEmbedProgress(`Embedding clauses... ${embedded}/${total}`);
        }
      );
      if (result.complete) {
        setEmbedProgress(`Done: ${result.totalEmbedded} clauses embedded`);
        setContractStatus('embedded');
      } else {
        setEmbedProgress(
          `Partial: ${result.totalEmbedded}/${result.totalClauses} clauses embedded. ${result.error || 'Retry to continue.'}`
        );
      }
    } catch (e) {
      console.error('[PipelineViewer] Failed to embed clauses:', e);
      setEmbedProgress(`Error: ${e instanceof Error ? e.message : 'Failed to embed clauses'}`);
    } finally {
      setActionLoading(null);
      await loadHealthData();
    }
  }, [contractId, loadHealthData]);

  const handleReextract = useCallback(async () => {
    setActionLoading('reextract');
    setBatchMessage(null);
    try {
      const result = await reextractMissingBatch(contractId);
      if (result.totalArticles === 0) {
        setBatchMessage(result.message || 'All articles already extracted.');
      } else {
        setBatchMessage(
          `Batch submitted: ${result.totalArticles} article${result.totalArticles !== 1 ? 's' : ''} queued for extraction.` +
          (result.batchId ? ` Batch ID: ${result.batchId.slice(0, 12)}...` : '') +
          ' Use batch-status and process-batch-results to complete.'
        );
      }
      // Refresh health after triggering re-extraction
      await loadHealthData();
    } catch (e) {
      console.error('[PipelineViewer] Failed to re-extract missing:', e);
      setBatchMessage(null);
      setHealthError(e instanceof Error ? e.message : 'Failed to re-extract missing articles');
    } finally {
      setActionLoading(null);
    }
  }, [contractId, loadHealthData]);

  // Load R2 files and contract detail on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setError(null);

      try {
        // Fetch file list, contract detail, and pipeline health in parallel
        const [fileListResult, contractDetail] = await Promise.all([
          fetchR2FileList(contractId).catch((e) => {
            console.warn('[PipelineViewer] Failed to load R2 files:', e);
            return { count: 0, objects: [] } as R2FileListResponse;
          }),
          fetchContractDetail(contractId).catch((e) => {
            console.warn('[PipelineViewer] Failed to load contract detail:', e);
            return null;
          }),
        ]);

        // Load health data in parallel (non-blocking)
        loadHealthData();

        // Set R2 files
        setR2Files(fileListResult.objects || []);

        // Build article summary list and full article data from contract detail (Supabase)
        const articles: ArticleSummary[] = [];
        const fullArticleMap = new Map<string, ExtractedArticleFromR2>();
        if (contractDetail?.articles) {
          for (const art of contractDetail.articles) {
            let clauseCount = 0;
            let charCount = 0;
            for (const sec of art.sections) {
              const clauses = sec.clauses || [];
              clauseCount += clauses.length;
              charCount += clauses.reduce((sum, c) => sum + (c.text?.length || 0), 0);
            }
            articles.push({
              number: art.number,
              title: art.title,
              sectionCount: art.sections.length,
              clauseCount,
              charCount,
            });
            // Store full article data for inline expansion (avoids stale R2 reads)
            fullArticleMap.set(art.number, {
              article: {
                number: art.number,
                title: art.title,
                summary: art.summary || '',
                sections: art.sections.map((s: any) => ({
                  number: s.number,
                  title: s.title,
                  clauses: (s.clauses || []).map((c: any) => ({
                    number: c.number || c.metadata?.number || undefined,
                    text: c.text,
                    cfrCitations: c.metadata?.cfrCitations || undefined,
                  })),
                })),
              },
              extractedAt: art.updated_at || art.created_at || new Date().toISOString(),
            });
          }
        }
        setArticleList(articles);
        setFullArticles(fullArticleMap);
        if (contractDetail?.contract?.status) {
          setContractStatus(contractDetail.contract.status);
        }

        // Update stats
        setStats({
          ocrChars: 0, // Will be set when OCR text is loaded
          totalArticles: contractDetail?.statistics?.totalArticles || articles.length,
          totalSections: contractDetail?.statistics?.totalSections || articles.reduce((s, a) => s + a.sectionCount, 0),
          totalClauses: contractDetail?.statistics?.totalClauses || articles.reduce((s, a) => s + a.clauseCount, 0),
          r2FileCount: fileListResult.count || fileListResult.objects?.length || 0,
        });
      } catch (e) {
        console.error('[PipelineViewer] Failed to load initial data:', e);
        setError(e instanceof Error ? e.message : 'Failed to load pipeline data');
      } finally {
        setLoading((prev) => ({ ...prev, files: false, articles: false }));
      }
    };

    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  // Load OCR text on demand
  const loadOcrText = useCallback(async () => {
    if (ocrText !== null) return; // Already loaded
    setLoading((prev) => ({ ...prev, ocr: true }));
    try {
      const text = await fetchProcessedText(contractId);
      setOcrText(text);
      setStats((prev) => ({ ...prev, ocrChars: text.length }));
    } catch (e) {
      console.error('[PipelineViewer] Failed to load OCR text:', e);
      setError(e instanceof Error ? e.message : 'Failed to load OCR text');
    } finally {
      setLoading((prev) => ({ ...prev, ocr: false }));
    }
  }, [contractId, ocrText]);

  // Toggle article expansion using Supabase data (falls back to R2 if not available)
  const handleToggleArticle = useCallback(
    async (articleNum: string) => {
      if (expandedArticle === articleNum) {
        setExpandedArticle(null);
        setExpandedArticleData(null);
        return;
      }

      setExpandedArticle(articleNum);
      setExpandedArticleData(null);

      // Use pre-loaded Supabase data if available (authoritative source)
      const cached = fullArticles.get(articleNum);
      if (cached) {
        setExpandedArticleData(cached);
        return;
      }

      // Fallback to R2 article-N.json (legacy)
      setExpandedArticleLoading(true);
      try {
        const data = await fetchExtractedArticle(contractId, articleNum);
        setExpandedArticleData(data);
      } catch (e) {
        console.error('[PipelineViewer] Failed to load article:', articleNum, e);
      } finally {
        setExpandedArticleLoading(false);
      }
    },
    [contractId, expandedArticle, fullArticles]
  );

  const totalClauseChars = articleList.reduce((sum, a) => sum + a.charCount, 0);

  return (
    <div className="space-y-6" data-testid="pipeline-viewer">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Extraction Pipeline</h2>
        <p className="text-sm text-gray-500">{contractName}</p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Summary Statistics */}
      <div
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
        data-testid="pipeline-stats"
      >
        <StatCard
          label="OCR Text"
          value={stats.ocrChars > 0 ? `${(stats.ocrChars / 1000).toFixed(0)}K` : '--'}
          loading={loading.ocr}
        />
        <StatCard label="Articles" value={healthData?.tocArticles ?? stats.totalArticles} loading={loading.articles} />
        <StatCard label="Sections" value={stats.totalSections} loading={loading.articles} />
        <StatCard label="Clauses" value={stats.totalClauses} loading={loading.articles} />
        <StatCard
          label="Clause Text"
          value={totalClauseChars > 0 ? `${(totalClauseChars / 1000).toFixed(0)}K` : '--'}
          loading={loading.articles}
        />
        <StatCard label="R2 Files" value={stats.r2FileCount} loading={loading.files} />
      </div>

      {/* Pipeline Health */}
      <PipelineHealthLayer
        contractId={contractId}
        health={healthData}
        loading={healthLoading}
        error={healthError}
        onRefresh={handleRefreshHealth}
        onRestore={handleRestore}
        onReextract={handleReextract}
        onEmbed={handleEmbed}
        actionLoading={actionLoading}
        batchMessage={batchMessage}
        embedProgress={embedProgress}
        contractStatus={contractStatus}
      />

      {/* Layer 1: OCR Output */}
      <OCRLayer
        contractId={contractId}
        ocrText={ocrText}
        ocrLoading={loading.ocr}
        onLoad={loadOcrText}
      />

      {/* Layer 2: R2 File Inventory */}
      <R2FilesLayer files={r2Files} loading={loading.files} />

      {/* Layer 3: Extracted Articles */}
      <ExtractedArticlesLayer
        articleList={articleList}
        loading={loading.articles}
        expandedArticle={expandedArticle}
        expandedArticleData={expandedArticleData}
        expandedArticleLoading={expandedArticleLoading}
        onToggleArticle={handleToggleArticle}
      />
    </div>
  );
};
